import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Paperclip, X, Send, Clock, Plus, GitBranch, MessageSquare, Timer, GripVertical, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import RichTextEditor, { RichTextDisplay } from "@/components/RichTextEditor";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, verticalListSortingStrategy, useSortable, arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const STATUS_OPTIONS = ["pending_review", "in_progress", "on_hold", "completed", "cancelled"];
const PRIORITY_OPTIONS = ["low", "medium", "high", "urgent"];
const STAGE_STATUSES = ["todo", "in_progress", "in_review", "completed"];
const STAGE_STATUS_FMT = { todo: "To Do", in_progress: "In Progress", in_review: "In Review", completed: "Completed" };
const STAGE_STATUS_COLORS = {
  todo: { bg: "#F5F3EF", text: "#57534E" },
  in_progress: { bg: "#DBEAFE", text: "#1D4ED8" },
  in_review: { bg: "#FEF3C7", text: "#D97706" },
  completed: { bg: "#D1FAE5", text: "#059669" },
};
const fmt = (s) => s?.split("_").map((w) => w[0].toUpperCase() + w.slice(1)).join(" ") || "";

const statusColors = {
  pending_review: { bg: "#FEF3C7", text: "#D97706" },
  in_progress: { bg: "#DBEAFE", text: "#1D4ED8" },
  completed: { bg: "#D1FAE5", text: "#059669" },
  cancelled: { bg: "#FEE2E2", text: "#DC2626" },
  on_hold: { bg: "#F3E8FF", text: "#7C3AED" },
};

const FieldRow = ({ label, children }) => (
  <div>
    <p className="text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: "#A8A29E" }}>{label}</p>
    {children}
  </div>
);

const selectCls = "w-full px-3 py-2 rounded-lg border text-sm outline-none focus:ring-1 focus:ring-amber-300 transition-all";
const selectStyle = { borderColor: "#E7E5E4", background: "#fff", color: "#292524" };

// ── Sortable stage row ────────────────────────────────────────────────────────
function SortableStageRow({ stage, timelogs, onUpdate, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: stage.id });
  const dndStyle = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1, borderColor: "#E7E5E4" };
  const logged = timelogs.filter((t) => t.stage_id === stage.id).reduce((s, t) => s + (t.hours || 0), 0);
  const sc = STAGE_STATUS_COLORS[stage.status] || STAGE_STATUS_COLORS.todo;

  return (
    <div
      ref={setNodeRef}
      style={dndStyle}
      className="flex items-center gap-3 bg-white rounded-xl border px-4 py-3"
      data-testid={`stage-row-${stage.id}`}
    >
      <button {...attributes} {...listeners} className="cursor-grab flex-shrink-0 touch-none" style={{ color: "#C7C3BF" }}>
        <GripVertical size={15} />
      </button>
      <select
        value={stage.status}
        onChange={(e) => onUpdate(stage.id, { status: e.target.value }, true)}
        className="text-xs px-2 py-1 rounded-full font-medium cursor-pointer border-0 flex-shrink-0"
        style={{ background: sc.bg, color: sc.text }}
        data-testid={`stage-status-${stage.id}`}
      >
        {STAGE_STATUSES.map((s) => <option key={s} value={s}>{STAGE_STATUS_FMT[s]}</option>)}
      </select>
      <input
        className="flex-1 text-sm outline-none bg-transparent min-w-0"
        style={{ color: "#292524" }}
        value={stage.title}
        onChange={(e) => onUpdate(stage.id, { title: e.target.value })}
        onBlur={() => onUpdate(stage.id, { title: stage.title }, true)}
        placeholder="Stage name..."
        data-testid={`stage-title-${stage.id}`}
      />
      <div className="flex items-center gap-4 flex-shrink-0">
        <div className="flex items-center gap-1 text-xs" style={{ color: "#A8A29E" }}>
          <Timer size={12} />
          <input
            type="number"
            min="0"
            step="0.5"
            value={stage.time_estimate || ""}
            onChange={(e) => onUpdate(stage.id, { time_estimate: parseFloat(e.target.value) || 0 })}
            onBlur={() => onUpdate(stage.id, { time_estimate: stage.time_estimate }, true)}
            className="w-12 outline-none bg-transparent text-right"
            style={{ color: "#A8A29E" }}
            placeholder="0"
            data-testid={`stage-estimate-${stage.id}`}
          />
          <span>h est</span>
        </div>
        <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: "#F0FDF4", color: "#059669" }}>
          {logged.toFixed(1)}h logged
        </span>
      </div>
      <button
        onClick={() => onDelete(stage.id)}
        className="p-1.5 rounded-lg hover:bg-red-50 flex-shrink-0 transition-colors"
        style={{ color: "#C7C3BF" }}
        data-testid={`stage-delete-${stage.id}`}
      >
        <Trash2 size={13} />
      </button>
    </div>
  );
}

export default function RequestDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [request, setRequest] = useState(null);
  const [messages, setMessages] = useState([]);
  const [timelogs, setTimelogs] = useState([]);
  const [activity, setActivity] = useState([]);
  const [team, setTeam] = useState([]);
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msgText, setMsgText] = useState("");
  const [msgAttachments, setMsgAttachments] = useState([]);
  const msgFileRef = useRef(null);
  const [sending, setSending] = useState(false);
  const [activeTab, setActiveTab] = useState("messages");
  const [showLogTime, setShowLogTime] = useState(false);
  const [localEst, setLocalEst] = useState("");
  const [localDesc, setLocalDesc] = useState("");
  const [logForm, setLogForm] = useState({
    hours: "", description: "", date: new Date().toISOString().slice(0, 10), is_billable: true, stage_id: "",
  });
  const [newStageTitle, setNewStageTitle] = useState("");
  const [addingStage, setAddingStage] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const messagesEndRef = useRef(null);

  const sensors = useSensors(useSensor(PointerSensor));

  useEffect(() => {
    Promise.all([
      api.get(`/requests/${id}`),
      api.get(`/requests/${id}/messages`),
      api.get(`/requests/${id}/timelogs`),
      api.get("/team"),
      api.get(`/requests/${id}/activity`),
    ])
      .then(([r, m, t, tm, act]) => {
        setRequest(r.data);
        setLocalEst(r.data.time_estimate_hours ?? "");
        setLocalDesc(r.data.description || "");
        setMessages(m.data);
        setTimelogs(t.data);
        setTeam(tm.data);
        setActivity(act.data);
        setStages(r.data.stages || []);
      })
      .catch(() => navigate("/requests"))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const updateField = async (field, value) => {
    const v = value === "" ? null : value;
    setRequest((prev) => ({ ...prev, [field]: v }));
    try {
      if (field === "assigned_to_id") {
        const assignee = team.find((t) => t.id === v);
        await api.put(`/requests/${id}`, { assigned_to_id: v || null });
        if (assignee) setRequest((prev) => ({ ...prev, assigned_to_name: assignee.name }));
      } else {
        await api.put(`/requests/${id}`, { [field]: v });
      }
    } catch {
      toast.error("Failed to save");
    }
  };

  const handleDeleteTask = async () => {
    try {
      await api.delete(`/requests/${id}`);
      toast.success("Task deleted");
      navigate("/requests");
    } catch {
      toast.error("Failed to delete task");
    }
  };

  // ── Stage handlers ──────────────────────────────────────────────────────────
  const addStage = async () => {
    if (!newStageTitle.trim()) return;
    try {
      const r = await api.post(`/requests/${id}/stages`, { title: newStageTitle.trim(), time_estimate: 0 });
      setStages((prev) => [...prev, r.data]);
      setNewStageTitle("");
      setAddingStage(false);
      toast.success("Stage added");
    } catch {
      toast.error("Failed to add stage");
    }
  };

  const updateStage = async (stageId, updates, commit = false) => {
    setStages((prev) => prev.map((s) => s.id === stageId ? { ...s, ...updates } : s));
    if (commit) {
      try {
        await api.put(`/requests/${id}/stages/${stageId}`, updates);
      } catch {
        toast.error("Failed to save stage");
      }
    }
  };

  const deleteStage = async (stageId) => {
    try {
      await api.delete(`/requests/${id}/stages/${stageId}`);
      setStages((prev) => prev.filter((s) => s.id !== stageId));
      toast.success("Stage removed");
    } catch {
      toast.error("Failed to delete stage");
    }
  };

  const handleStageDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = stages.findIndex((s) => s.id === active.id);
    const newIndex = stages.findIndex((s) => s.id === over.id);
    const reordered = arrayMove(stages, oldIndex, newIndex).map((s, i) => ({ ...s, order: i }));
    setStages(reordered);
    try {
      await api.put(`/requests/${id}/stages`, reordered);
    } catch {
      toast.error("Failed to save order");
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!msgText.trim() && msgAttachments.length === 0) return;
    setSending(true);
    try {
      const r = await api.post(`/requests/${id}/messages`, { content: msgText, attachments: msgAttachments });
      setMessages((prev) => [...prev, r.data]);
      setMsgText("");
      setMsgAttachments([]);
    } catch {
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const handleMsgFile = (e) => {
    Array.from(e.target.files || []).forEach((file) => {
      if (file.size > 10 * 1024 * 1024) { toast.error(`${file.name} too large (max 10 MB)`); return; }
      const reader = new FileReader();
      reader.onload = (ev) => {
        const b64 = ev.target.result.split(",")[1];
        setMsgAttachments((p) => [...p, { filename: file.name, content_type: file.type, data_b64: b64 }]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = "";
  };

  const logTime = async (e) => {
    e.preventDefault();
    const payload = {
      request_id: id,
      hours: parseFloat(logForm.hours),
      description: logForm.description,
      date: logForm.date,
      is_billable: logForm.is_billable,
    };
    if (stages.length > 0) {
      if (!logForm.stage_id) {
        toast.error("Please select a stage to log time against");
        return;
      }
      payload.stage_id = logForm.stage_id;
    }
    try {
      const r = await api.post("/timelogs", payload);
      setTimelogs((prev) => [r.data, ...prev]);
      setShowLogTime(false);
      setLogForm({ hours: "", description: "", date: new Date().toISOString().slice(0, 10), is_billable: true, stage_id: "" });
      toast.success("Time logged");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to log time");
    }
  };

  if (loading) return <Layout><div className="p-8 text-sm" style={{ color: "#A8A29E" }}>Loading...</div></Layout>;
  if (!request) return null;

  const sc = statusColors[request.status] || { bg: "#F5F3EF", text: "#57534E" };
  const totalLogged = timelogs.reduce((s, t) => s + (t.hours || 0), 0);
  const totalStageEst = stages.reduce((s, st) => s + (st.time_estimate || 0), 0);

  const tabs = [
    { key: "messages", label: `Messages (${messages.length})` },
    { key: "stages", label: `Stages (${stages.length})` },
    { key: "time-logs", label: `Time Logs (${totalLogged.toFixed(1)}h)` },
    { key: "activity", label: "Activity" },
  ];

  return (
    <>
    <Layout>
      <div className="p-4 sm:p-6 lg:p-8" data-testid="request-detail-page">
        <div className="flex items-start gap-4 mb-6">
          <button onClick={() => navigate("/requests")} className="mt-1 p-1.5 rounded-lg hover:bg-white" style={{ color: "#A8A29E" }}>
            <ArrowLeft size={18} strokeWidth={1.5} />
          </button>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h1 className="font-serif text-2xl font-bold" style={{ color: "#292524" }}>{request.title}</h1>
              <span className="text-xs font-medium px-2.5 py-1 rounded-full" style={{ background: sc.bg, color: sc.text }}>
                {fmt(request.status)}
              </span>
            </div>
            <p className="text-sm" style={{ color: "#57534E" }}>{request.client_name} · Submitted by {request.submitted_by_name}</p>
          </div>
          <button
            data-testid="delete-task-button"
            onClick={() => setShowDeleteConfirm(true)}
            className="mt-1 p-1.5 rounded-lg hover:bg-red-50 transition-colors flex-shrink-0"
            style={{ color: "#DC2626" }}
            title="Delete task"
          >
            <Trash2 size={16} strokeWidth={1.5} />
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* ── Main Content ─────────────────────────────────────────────────── */}
          <div className="lg:col-span-2">
            {/* Tabs */}
            <div className="flex gap-1 mb-4" style={{ borderBottom: "1px solid #E7E5E4" }}>
              {tabs.map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => setActiveTab(key)}
                  className={`px-4 py-2 text-sm font-medium transition-colors ${activeTab === key ? "border-b-2 border-stone-800" : ""}`}
                  style={{ color: activeTab === key ? "#292524" : "#A8A29E", marginBottom: "-1px" }}
                  data-testid={`tab-${key}`}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* ── Messages Tab ── */}
            {activeTab === "messages" && (
              <div>
                <div className="space-y-4 mb-4 max-h-96 overflow-y-auto pr-1">
                  {messages.length === 0 && (
                    <p className="text-sm text-center py-8" style={{ color: "#A8A29E" }}>No messages yet.</p>
                  )}
                  {messages.map((msg) => {
                    const isAgency = msg.sender_type === "agency";
                    const initial = msg.sender_name?.[0]?.toUpperCase() || "?";
                    return (
                      <div key={msg.id} className="flex gap-3 items-start">
                        <div
                          className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-white text-xs font-semibold mt-0.5"
                          style={{ background: isAgency ? "#292524" : "#D97706" }}
                        >
                          {initial}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline gap-2 mb-1">
                            <span className="text-sm font-semibold" style={{ color: "#292524" }}>{msg.sender_name}</span>
                            <span className="text-xs" style={{ color: "#C7C3BF" }}>{msg.created_at?.slice(0, 16).replace("T", " ")}</span>
                          </div>
                          <div className="rounded-xl px-4 py-3" style={{ background: isAgency ? "#fff" : "#FAFAF9", border: "1px solid #E7E5E4" }}>
                            <RichTextDisplay html={msg.content} />
                            {msg.attachments?.length > 0 && (
                              <div className="mt-2 space-y-1">
                                {msg.attachments.map((att, i) => (
                                  <a
                                    key={i}
                                    href={`data:${att.content_type};base64,${att.data_b64}`}
                                    download={att.filename}
                                    className="flex items-center gap-1.5 text-xs px-2 py-1.5 rounded-lg hover:bg-stone-50"
                                    style={{ color: "#0284C7", border: "1px solid #E7E5E4" }}
                                  >
                                    <Paperclip size={11} />{att.filename}
                                  </a>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>
                {/* Composer */}
                {msgAttachments.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {msgAttachments.map((att, i) => (
                      <div key={i} className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs" style={{ background: "#F5F3EF", color: "#292524" }}>
                        <Paperclip size={10} />
                        <span className="max-w-[120px] truncate">{att.filename}</span>
                        <button onClick={() => setMsgAttachments((p) => p.filter((_, j) => j !== i))} style={{ color: "#A8A29E" }}><X size={10} /></button>
                      </div>
                    ))}
                  </div>
                )}
                <RichTextEditor value={msgText} onChange={setMsgText} placeholder="Write a message..." minHeight={72} />
                <div className="flex items-center justify-between mt-2">
                  <div>
                    <input ref={msgFileRef} type="file" multiple className="hidden" onChange={handleMsgFile} />
                    <button
                      type="button"
                      onClick={() => msgFileRef.current?.click()}
                      className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border hover:bg-stone-50"
                      style={{ borderColor: "#E7E5E4", color: "#57534E" }}
                      data-testid="attach-message-button"
                    >
                      <Paperclip size={12} />Attach
                    </button>
                  </div>
                  <Button
                    data-testid="send-message-button"
                    size="sm"
                    disabled={sending || (!msgText.trim() && msgAttachments.length === 0)}
                    onClick={(e) => sendMessage(e)}
                    style={{ background: "#292524", color: "#fff" }}
                  >
                    <Send size={13} className="mr-1" />Send
                  </Button>
                </div>
              </div>
            )}

            {/* ── Stages Tab ── */}
            {activeTab === "stages" && (
              <div data-testid="stages-section">
                {stages.length > 0 && (
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs" style={{ color: "#A8A29E" }}>
                      {stages.length} stage{stages.length !== 1 ? "s" : ""} · {totalStageEst}h estimated total
                    </p>
                  </div>
                )}

                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleStageDragEnd}>
                  <SortableContext items={stages.map((s) => s.id)} strategy={verticalListSortingStrategy}>
                    <div className="space-y-2">
                      {stages.length === 0 && !addingStage && (
                        <p className="text-sm text-center py-8" style={{ color: "#A8A29E" }}>
                          No stages yet. Break this request into smaller pieces.
                        </p>
                      )}
                      {stages.map((stage) => (
                        <SortableStageRow
                          key={stage.id}
                          stage={stage}
                          timelogs={timelogs}
                          onUpdate={updateStage}
                          onDelete={deleteStage}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>

                {addingStage ? (
                  <div className="flex gap-2 mt-3">
                    <input
                      autoFocus
                      className="flex-1 text-sm px-3 py-2 rounded-lg border outline-none focus:ring-1 focus:ring-amber-300"
                      style={{ borderColor: "#E7E5E4" }}
                      value={newStageTitle}
                      onChange={(e) => setNewStageTitle(e.target.value)}
                      placeholder="Stage name..."
                      onKeyDown={(e) => {
                        if (e.key === "Enter") addStage();
                        if (e.key === "Escape") { setAddingStage(false); setNewStageTitle(""); }
                      }}
                      data-testid="new-stage-input"
                    />
                    <Button
                      size="sm"
                      onClick={addStage}
                      style={{ background: "#292524", color: "#fff" }}
                      data-testid="confirm-add-stage-button"
                    >
                      Add
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { setAddingStage(false); setNewStageTitle(""); }}
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    className="mt-3 w-full"
                    onClick={() => setAddingStage(true)}
                    data-testid="add-stage-button"
                  >
                    <Plus size={13} className="mr-1" />Add Stage
                  </Button>
                )}
              </div>
            )}

            {/* ── Time Logs Tab ── */}
            {activeTab === "time-logs" && (
              <div>
                <div className="flex justify-end mb-3">
                  <Button
                    data-testid="log-time-button"
                    size="sm"
                    onClick={() => setShowLogTime(!showLogTime)}
                    style={{ background: "#292524", color: "#fff" }}
                  >
                    <Clock size={14} className="mr-1" />Log Time
                  </Button>
                </div>
                {showLogTime && (
                  <form onSubmit={logTime} className="bg-white rounded-xl border p-4 mb-4 space-y-3" style={{ borderColor: "#E7E5E4" }}>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label>Hours *</Label>
                        <Input
                          data-testid="log-hours-input"
                          type="number" min="0.1" step="0.1" required
                          value={logForm.hours}
                          onChange={(e) => setLogForm({ ...logForm, hours: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label>Date *</Label>
                        <Input
                          type="date" required
                          value={logForm.date}
                          onChange={(e) => setLogForm({ ...logForm, date: e.target.value })}
                        />
                      </div>
                      <div className="flex items-end pb-2 gap-2">
                        <input
                          type="checkbox" id="billable"
                          checked={logForm.is_billable}
                          onChange={(e) => setLogForm({ ...logForm, is_billable: e.target.checked })}
                        />
                        <Label htmlFor="billable">Billable</Label>
                      </div>
                    </div>
                    {stages.length > 0 && (
                      <div>
                        <Label>Stage *</Label>
                        <select
                          required
                          className={selectCls}
                          style={selectStyle}
                          value={logForm.stage_id}
                          onChange={(e) => setLogForm({ ...logForm, stage_id: e.target.value })}
                          data-testid="log-stage-select"
                        >
                          <option value="">Select a stage...</option>
                          {stages.map((s) => (
                            <option key={s.id} value={s.id}>{s.title}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    <div>
                      <Label>Description</Label>
                      <Input
                        value={logForm.description}
                        onChange={(e) => setLogForm({ ...logForm, description: e.target.value })}
                        placeholder="What did you work on?"
                      />
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button type="button" variant="outline" size="sm" onClick={() => setShowLogTime(false)}>Cancel</Button>
                      <Button type="submit" size="sm" style={{ background: "#292524", color: "#fff" }}>Log Time</Button>
                    </div>
                  </form>
                )}
                <div className="space-y-2">
                  {timelogs.length === 0 && (
                    <p className="text-sm text-center py-8" style={{ color: "#A8A29E" }}>No time logged yet</p>
                  )}
                  {timelogs.map((log) => (
                    <div key={log.id} className="bg-white rounded-xl border p-4 flex items-center justify-between" style={{ borderColor: "#E7E5E4" }}>
                      <div>
                        <p className="text-sm font-medium" style={{ color: "#292524" }}>{log.user_name} — {log.hours}h</p>
                        <p className="text-xs mt-0.5" style={{ color: "#A8A29E" }}>
                          {log.date}
                          {log.stage_title && ` · ${log.stage_title}`}
                          {log.description && ` · ${log.description}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {log.is_billable && (
                          <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: "#D1FAE5", color: "#059669" }}>Billable</span>
                        )}
                        <span className="text-sm font-semibold" style={{ color: "#292524" }}>{log.hours}h</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Activity Tab ── */}
            {activeTab === "activity" && (
              <div data-testid="activity-timeline" className="space-y-0">
                {activity.length === 0 ? (
                  <p className="text-sm text-center py-8" style={{ color: "#A8A29E" }}>No activity yet</p>
                ) : (
                  <div className="relative pl-6">
                    <div className="absolute left-2 top-2 bottom-2 w-px" style={{ background: "#E7E5E4" }} />
                    {activity.map((item, i) => {
                      let icon, iconBg, iconColor, title, body;
                      if (item.type === "activity") {
                        icon = <GitBranch size={11} />;
                        iconBg = "#F5F3EF"; iconColor = "#A8A29E";
                        title = item.user_name;
                        body = item.detail;
                      } else if (item.type === "message") {
                        icon = <MessageSquare size={11} />;
                        iconBg = item.sender_type === "agency" ? "#292524" : "#FEF3C7";
                        iconColor = item.sender_type === "agency" ? "#fff" : "#D97706";
                        title = item.user_name;
                        body = item.content;
                      } else {
                        icon = <Timer size={11} />;
                        iconBg = "#D1FAE5"; iconColor = "#059669";
                        title = item.user_name;
                        body = `Logged ${item.hours}h${item.description ? ` — ${item.description}` : ""}`;
                      }
                      return (
                        <div key={i} data-testid={`activity-item-${i}`} className="flex gap-3 mb-4 relative">
                          <div
                            className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center z-10"
                            style={{ background: iconBg, color: iconColor, marginLeft: "-2px" }}
                          >
                            {icon}
                          </div>
                          <div className="flex-1 pb-1">
                            <div className="flex items-baseline gap-2 flex-wrap">
                              <span className="text-xs font-semibold" style={{ color: "#292524" }}>{title}</span>
                              <span className="text-xs" style={{ color: "#A8A29E" }}>{item.timestamp?.slice(0, 16).replace("T", " ")}</span>
                            </div>
                            <p className="text-sm mt-0.5" style={{ color: "#57534E" }}>{body}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── Sidebar ─────────────────────────────────────────────────────── */}
          <div className="space-y-4">
            <div className="bg-white rounded-xl border p-5 space-y-4" style={{ borderColor: "#E7E5E4" }}>
              <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: "#A8A29E" }}>Details</p>

              <FieldRow label="Status">
                <select
                  data-testid="status-select"
                  value={request.status}
                  onChange={(e) => updateField("status", e.target.value)}
                  className={selectCls} style={selectStyle}
                >
                  {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{fmt(s)}</option>)}
                </select>
              </FieldRow>

              <FieldRow label="Priority">
                <select
                  data-testid="priority-select"
                  value={request.priority || "medium"}
                  onChange={(e) => updateField("priority", e.target.value)}
                  className={selectCls} style={selectStyle}
                >
                  {PRIORITY_OPTIONS.map((p) => <option key={p} value={p}>{p[0].toUpperCase() + p.slice(1)}</option>)}
                </select>
              </FieldRow>

              <FieldRow label="Time Estimate (h)">
                <input
                  data-testid="time-estimate-input"
                  type="number" min="0" step="0.5"
                  value={localEst}
                  onChange={(e) => setLocalEst(e.target.value)}
                  onBlur={() => updateField("time_estimate_hours", localEst ? parseFloat(localEst) : null)}
                  className={selectCls}
                  style={selectStyle}
                  placeholder="—"
                />
              </FieldRow>

              <FieldRow label="Assigned To">
                <select
                  value={request.assigned_to_id || ""}
                  onChange={(e) => updateField("assigned_to_id", e.target.value)}
                  className={selectCls} style={selectStyle}
                >
                  <option value="">Unassigned</option>
                  {team.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </FieldRow>

              <FieldRow label="Due Date">
                <input
                  data-testid="due-date-input"
                  type="date"
                  value={request.due_date || ""}
                  onChange={(e) => updateField("due_date", e.target.value || null)}
                  className={selectCls}
                  style={{ ...selectStyle, colorScheme: "light" }}
                />
              </FieldRow>
            </div>

            <div className="bg-white rounded-xl border p-5" style={{ borderColor: "#E7E5E4" }}>
              <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: "#A8A29E" }}>Description</p>
              <RichTextEditor
                value={localDesc}
                onChange={setLocalDesc}
                placeholder="Add a description..."
                minHeight={80}
              />
              {localDesc !== (request.description || "") && (
                <div className="flex justify-end mt-2">
                  <Button size="sm" onClick={() => updateField("description", localDesc)} style={{ background: "#292524", color: "#fff" }}>
                    Save
                  </Button>
                </div>
              )}
            </div>

            <div className="bg-white rounded-xl border p-5" style={{ borderColor: "#E7E5E4" }}>
              <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: "#A8A29E" }}>Time Summary</p>
              <div className="flex justify-between text-sm">
                <span style={{ color: "#57534E" }}>Total Logged</span>
                <span className="font-semibold" style={{ color: "#292524" }}>{totalLogged.toFixed(1)}h</span>
              </div>
              {(localEst || request.time_estimate_hours) > 0 && (
                <div className="mt-2">
                  <div className="flex justify-between text-xs mb-1" style={{ color: "#A8A29E" }}>
                    <span>Progress</span>
                    <span>{Math.round(totalLogged / (parseFloat(localEst) || request.time_estimate_hours) * 100)}%</span>
                  </div>
                  <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "#E7E5E4" }}>
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${Math.min(100, totalLogged / (parseFloat(localEst) || request.time_estimate_hours) * 100)}%`,
                        background: "#D97706",
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>

    <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
      <AlertDialogContent data-testid="delete-task-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Task</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete <strong>"{request?.title}"</strong>? This will also remove all messages, time logs, and stages. This cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="delete-task-cancel-btn">Cancel</AlertDialogCancel>
          <AlertDialogAction
            data-testid="delete-task-confirm-btn"
            onClick={handleDeleteTask}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
